import { FC, Fragment, ReactNode } from 'react';
import { IXrayAnalysis, IXraySpanItem } from 'src/ts/interfaces/xray';
import {
  ListCheckCompareGrid,
  ListCheckCompareSide,
  ListCheckNavButton,
  TextComparePageCell,
  TextCompareRef,
  TextCompareScroll,
  TextCompareSwatch,
  TextCompareTable,
  XrayRawText
} from '../../styles';
import { listCheckCopy } from './listCheckCopy';

export type TextSideStatus = 'idle' | 'loading' | 'success' | 'error' | 'unavailable';

export interface TextSide {
  title: string;
  aside?: string | null;
  status: TextSideStatus;
  data: IXrayAnalysis | null;
  onRetry?: () => void;
}

interface TextPropertiesCompareProps {
  document: TextSide;
  candidate: TextSide;
}

const HEX = /^[0-9a-f]{6}$/i;

const pagesOf = (side: TextSide): Map<number, IXraySpanItem[]> => {
  const pages = new Map<number, IXraySpanItem[]>();
  if (side.status !== 'success') return pages;
  (side.data?.text_breakups?.spans ?? []).forEach((page) => {
    pages.set(page.page, [...(pages.get(page.page) ?? []), ...(page.items ?? [])]);
  });
  return pages;
};

export const messageOf = (side: TextSide, empty: boolean, emptyText: string): ReactNode => {
  if (side.status === 'success') return empty ? emptyText : null;
  if (side.status === 'unavailable') return listCheckCopy.noExampleDocument;
  if (side.status === 'error') {
    return (
      <>
        {listCheckCopy.xrayFailed}{' '}
        {side.onRetry && (
          <ListCheckNavButton type="button" onClick={side.onRetry}>
            {listCheckCopy.retry}
          </ListCheckNavButton>
        )}
      </>
    );
  }
  return listCheckCopy.analyzing;
};

const Side: FC<{ side: TextSide; testId: string }> = ({ side, testId }) => {
  const pages = pagesOf(side);
  const message = messageOf(side, !pages.size, listCheckCopy.noTextFound);
  return (
    <ListCheckCompareSide data-testid={testId}>
      <h4>
        {side.title}
        {side.aside && <TextCompareRef>{side.aside}</TextCompareRef>}
      </h4>
      {message ? (
        <p>{message}</p>
      ) : (
        <TextCompareScroll
          data-testid="text-compare-scroll"
          role="region"
          aria-label={side.title}
          tabIndex={0}
        >
          <TextCompareTable>
            <colgroup>
              <col style={{ width: '30%' }} />
              <col style={{ width: '82px' }} />
              <col />
            </colgroup>
            <thead>
              <tr>
                <th>{listCheckCopy.font}</th>
                <th>{listCheckCopy.color}</th>
                <th>{listCheckCopy.text}</th>
              </tr>
            </thead>
            <tbody>
              {Array.from(pages.entries())
                .sort(([a], [b]) => a - b)
                .map(([page, items]) => (
                  <Fragment key={page}>
                    <tr>
                      <TextComparePageCell colSpan={3}>
                        {listCheckCopy.page(page)}
                      </TextComparePageCell>
                    </tr>
                    {items.map((item, index) => (
                      <tr key={index} data-testid="text-compare-row">
                        <td>{item.font}</td>
                        <td>
                          {HEX.test(item.color) && <TextCompareSwatch $color={`#${item.color}`} />}
                          {item.color}
                        </td>
                        <XrayRawText>{item.text}</XrayRawText>
                      </tr>
                    ))}
                  </Fragment>
                ))}
            </tbody>
          </TextCompareTable>
        </TextCompareScroll>
      )}
    </ListCheckCompareSide>
  );
};

const TextPropertiesCompare: FC<TextPropertiesCompareProps> = ({ document, candidate }) => (
  <ListCheckCompareGrid data-testid="text-properties-compare">
    <Side side={document} testId="text-compare-document" />
    <Side side={candidate} testId="text-compare-candidate" />
  </ListCheckCompareGrid>
);

export default TextPropertiesCompare;
